/**
 * The global player store — §4.1, §5.4a.
 *
 * Lives ABOVE THE ROUTER (L11 / A1). Every mutation delegates to the pure reducer in
 * @mushaf/corpus; this file adds no logic of its own, so the reducer's tests really
 * are tests of what runs in the browser.
 */
import { create } from 'zustand'
import {
  armSeek,
  confineQueue as confineQueueR,
  consumeAyahRepeat as consumeAyahRepeatR,
  consumePassageRepeat as consumePassageRepeatR,
  consumeSeek as consumeSeekR,
  extendQueue as extendQueueR,
  initialPlayerState,
  next as nextR,
  pause as pauseR,
  play as playR,
  previous as previousR,
  resume as resumeR,
  resyncTo as resyncToR,
  setIndex as setIndexR,
  setRepeat as setRepeatR,
  stop as stopR,
  trimQueue as trimQueueR,
  type PlayerState,
  type QueueKind,
  type RepeatSettings,
} from '@mushaf/corpus'

interface PlayerActions {
  play: (queue: readonly string[], startKey: string | undefined, kind: QueueKind) => void
  pause: () => void
  resume: () => void
  stop: () => void
  next: () => void
  previous: () => void
  setIndex: (index: number) => void
  /**
   * INTERNAL — call `useSettings.setRepeatAyah` / `setRepeatPassage` instead.
   *
   * The repeat COUNT is a persisted preference owned by `stores/settings.ts`; the copy
   * in `PlayerState.repeat` is a mirror the reducer needs in order to re-arm its
   * counters (A19), in the same sense that `currentKey` mirrors `queue[index]`. Calling
   * this directly from the UI would put the mirror out of step with the preference and
   * the divergence would only show up after a reload.
   */
  setRepeat: (patch: Partial<RepeatSettings>) => void
  consumeAyahRepeat: () => void
  consumePassageRepeat: () => void
  extendQueue: (keys: readonly string[]) => void
  trimQueue: (length: number) => void
  confineQueue: (from: number, to: number) => void
  resyncTo: (queue: readonly string[], key: string) => void
  armSeek: (ms: number | null) => void
  consumeSeek: () => void
}

export type PlayerStore = PlayerState & PlayerActions

export const usePlayer = create<PlayerStore>()((set) => ({
  ...initialPlayerState,

  play: (queue, startKey, kind) => set((s) => playR(s, queue, startKey, kind)),
  pause: () => set(pauseR),
  resume: () => set(resumeR),
  stop: () => set(stopR),
  next: () => set(nextR),
  previous: () => set(previousR),
  setIndex: (index) => set((s) => setIndexR(s, index)),
  setRepeat: (patch) => set((s) => setRepeatR(s, patch)),
  consumeAyahRepeat: () => set(consumeAyahRepeatR),
  consumePassageRepeat: () => set(consumePassageRepeatR),
  extendQueue: (keys) => set((s) => extendQueueR(s, keys)),
  trimQueue: (length) => set((s) => trimQueueR(s, length)),
  confineQueue: (from, to) => set((s) => confineQueueR(s, from, to)),
  resyncTo: (queue, key) => set((s) => resyncToR(s, queue, key)),
  armSeek: (ms) => set((s) => armSeek(s, ms)),
  consumeSeek: () => set(consumeSeekR),
}))

/** Selectors — components subscribe to the narrowest slice they need. */
export const selectCurrentKey = (s: PlayerStore) => s.currentKey
export const selectIsPlaying = (s: PlayerStore) => s.isPlaying
export const selectRepeat = (s: PlayerStore) => s.repeat
